import React, { useState } from 'react';
import {
    Calendar,
    Clock,
    FileText,
    ChevronRight,
    ChevronDown,
    Info,
    Building2,
    Search,
    AlertCircle,
    X,
    Wallet,
    MapPin,
    Loader2,
} from "lucide-react";
import moment from 'moment';
import { useGetUserOwnContracts } from '../../custom-hooks/use-contracts';
import { convertToPresentableText } from '../../utils/functions';
import ErrorX from '../../components/error-x';

const STATUS_BADGE = {
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    completed: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-red-100 text-red-700',
};

const statusLabel = (status) => status ? status.charAt(0).toUpperCase() + status.slice(1) : '';

const formatDate = (date) => {
    if (!date) return 'Ongoing';
    return moment(date).format('MMM DD, YYYY');
};

const formatDuration = (startDate, endDate) => {
    if (!endDate) return 'Permanent';
    const start = moment(startDate);
    const end = moment(endDate);
    const months = end.diff(start, 'months');
    if (months >= 1) return `${months} month${months !== 1 ? 's' : ''}`;
    const days = end.diff(start, 'days');
    return `${days} day${days !== 1 ? 's' : ''}`;
};

const getContractSecurity = (contract) => {
    const bookingSecurity = (contract?.bookings || []).reduce((sum, booking) => sum + Number(booking?.securityAmount || 0), 0);
    if (bookingSecurity > 0) {
        return bookingSecurity;
    }
    return Number(contract?.securityDeposit ?? contract?.totalAdvanceRent ?? 0);
};

const getContractAdvance = (contract) => {
    return (contract?.bookings || []).reduce((sum, booking) => sum + Number(booking?.advanceAmount || 0), 0);
};

const TAB_META = {
    active: { label: 'Active', emptyIcon: Building2 },
    pending: { label: 'Pending', emptyIcon: Clock },
    completed: { label: 'Completed', emptyIcon: Calendar },
    cancelled: { label: 'Cancelled', emptyIcon: AlertCircle },
};
const TAB_ORDER = ['active', 'pending', 'completed', 'cancelled'];

const UserContractsPage = () => {
    const { data: contracts, isLoading, isError, refetch } = useGetUserOwnContracts();
    const [activeTab, setActiveTab] = useState("active");
    const [searchQuery, setSearchQuery] = useState('');

    const filteredContracts = contracts?.filter(contract => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
            contract._id.toLowerCase().includes(searchLower) ||
            (contract.comment && contract.comment.toLowerCase().includes(searchLower)) ||
            (contract.user?.name && contract.user.name.toLowerCase().includes(searchLower)) ||
            (contract.contractType && contract.contractType.toLowerCase().includes(searchLower))
        );
    }) || [];

    const activeContracts = filteredContracts.filter(contract =>
        contract.status === 'approved' &&
        (!contract.endDate || new Date(contract.endDate) > new Date()) &&
        (!contract.void || !contract.void.isVoid)
    );
    const completedContracts = filteredContracts.filter(contract =>
        contract.status === 'completed' ||
        (contract.endDate && new Date(contract.endDate) <= new Date())
    );
    const pendingContracts = filteredContracts.filter(contract => contract.status === 'pending');
    const cancelledContracts = filteredContracts.filter(contract =>
        contract.status === 'rejected' ||
        contract.status === 'cancelled' ||
        (contract.void && contract.void.isVoid)
    );

    const listByTab = {
        active: activeContracts,
        pending: pendingContracts,
        completed: completedContracts,
        cancelled: cancelledContracts,
    };
    const counts = {
        active: activeContracts.length,
        pending: pendingContracts.length,
        completed: completedContracts.length,
        cancelled: cancelledContracts.length,
    };
    const currentList = listByTab[activeTab] || [];
    const activeIndex = TAB_ORDER.indexOf(activeTab);

    if (isLoading) {
        return (
            <div className="flex h-[60vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
        );
    }

    if (isError) {
        return (
            <ErrorX
                title="Error loading contracts"
                message="There was an error loading your contracts. Please try again later."
                onRetry={refetch}
            />
        );
    }

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="font-display text-[25px] font-bold text-ink">My Contracts</h1>
                    <p className="mt-1 text-sm text-[#8C8175]">Your workspace agreements with WorkBench.</p>
                </div>
                {/* Not wired to any action yet — re-enable once contract requests are supported.
                <button
                    title="Request a new contract (coming soon)"
                    className="flex items-center gap-2 rounded-[11px] bg-brand px-4 py-2.5 font-display text-[13px] font-semibold text-white shadow-[0_8px_16px_-10px_rgba(190,83,16,0.7)] transition-colors hover:bg-brand-dark"
                >
                    <Plus size={18} />
                    Request New Contract
                </button>
                */}
            </div>

            {/* Tabs + search */}
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative grid w-full max-w-[480px] grid-cols-4 rounded-xl border border-[#ECE3D5] bg-white p-[5px]">
                    <span
                        className="pointer-events-none absolute inset-y-[5px] left-[5px] w-[calc(25%-2.5px)] rounded-[9px] bg-brand shadow-[0_6px_14px_-8px_rgba(190,83,16,0.7)] transition-transform duration-[340ms] ease-[cubic-bezier(.4,0,.2,1)]"
                        style={{ transform: `translateX(${activeIndex * 100}%)` }}
                    />
                    {TAB_ORDER.map((key) => {
                        const active = activeTab === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={`relative z-10 flex items-center justify-center gap-1.5 rounded-[9px] px-2 py-2 text-[13px] font-semibold transition-colors ${active ? 'text-white' : 'text-[#6B675F] hover:text-ink'}`}
                            >
                                {TAB_META[key].label}
                                <span className={`rounded-full px-[7px] py-px text-[11px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-cream-input text-brand'}`}>
                                    {counts[key]}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="relative w-full sm:w-64">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B7AC9E]" />
                    <input
                        type="text"
                        placeholder="Search contracts..."
                        className="w-full rounded-xl border-[1.5px] border-cream-200 bg-white py-2.5 pl-9 pr-9 text-sm text-[#2B2420] outline-none transition-colors placeholder:text-[#B7AC9E] focus:border-brand"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#B7AC9E] hover:text-ink"
                            onClick={() => setSearchQuery('')}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Contract list */}
            <div key={activeTab} className="animate-fadeIn space-y-4">
                {currentList.length > 0 ? (
                    currentList.map(contract => <ContractCard key={contract._id} contract={contract} />)
                ) : (
                    <EmptyState
                        message={searchQuery ? `No matching ${activeTab} contracts found` : `You don't have any ${activeTab} contracts`}
                        icon={searchQuery ? Search : TAB_META[activeTab].emptyIcon}
                    />
                )}
            </div>
        </div>
    );
};

export default UserContractsPage;

// Stat cell in the contract card
const Stat = ({ label, value }) => (
    <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#A89C8C]">{label}</div>
        <div className="mt-1 font-medium text-ink">{value}</div>
    </div>
);

const ContractCard = ({ contract }) => {
    const [expanded, setExpanded] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    const hasOffice = (contract.bookings || []).some(b => b.office);
    const CardIcon = hasOffice ? Building2 : FileText;
    const security = getContractSecurity(contract);
    const advance = getContractAdvance(contract);
    const id6 = contract._id.slice(-6).toUpperCase();

    const hasMoreDetails = !!(
        contract.comment ||
        contract.reason ||
        (contract.meetingRoomQuota && contract.meetingRoomQuota.monthlyHours > 0) ||
        (contract.dayPassConfig && contract.dayPassConfig.type && contract.dayPassConfig.type !== 'none') ||
        (contract.void && contract.void.isVoid)
    );

    return (
        <div className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
            {/* Top */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3.5">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
                        <CardIcon size={20} />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-display text-base font-semibold text-ink">Contract #{id6}</h3>
                            <span className="rounded-full bg-cream-input px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9F440B]">
                                {contract.contractType === 'permanent' ? 'Permanent' : 'Temporary'}
                            </span>
                        </div>
                        <p className="mt-0.5 text-xs text-[#A89C8C]">Created on {formatDate(contract.createdDate || contract.startDate)}</p>
                    </div>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGE[contract.status] || 'bg-cream-100 text-[#6B675F]'}`}>
                    {statusLabel(contract.status)}
                </span>
            </div>

            <hr className="my-5 border-[#F1E9DC]" />

            {/* Stats */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
                <Stat label="Start" value={formatDate(contract.startDate)} />
                <Stat label="End" value={formatDate(contract.endDate)} />
                <Stat label="Duration" value={formatDuration(contract.startDate, contract.endDate)} />
                <Stat label="Notice Period" value={contract.noticePeriod ? `${contract.noticePeriod} weeks` : '—'} />
                <Stat label="Security" value={`PKR ${security.toLocaleString()}`} />
            </div>

            {/* Footer */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                {advance > 0 ? (
                    <span className="inline-flex items-center gap-2 rounded-lg bg-cream-input px-3 py-1.5 text-sm font-medium text-brand">
                        <Wallet size={16} />
                        Advance paid · PKR {advance.toLocaleString()}
                    </span>
                ) : <span />}

                <div className="flex flex-wrap gap-2">
                    {hasMoreDetails && (
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="flex items-center gap-1.5 rounded-lg border border-[#ECE3D5] bg-white px-3.5 py-2 text-sm font-medium text-[#6B675F] transition-colors hover:bg-cream"
                        >
                            {showDetails ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            {showDetails ? 'Hide details' : 'Show more details'}
                        </button>
                    )}
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="flex items-center gap-1.5 rounded-lg bg-cream-input px-3.5 py-2 text-sm font-semibold text-brand transition-colors hover:bg-[#F3D9C2]"
                    >
                        <Calendar size={16} />
                        {expanded ? 'Hide booking details' : `View booking details${contract.bookings && contract.bookings.length > 1 ? ` (${contract.bookings.length})` : ''}`}
                    </button>
                </div>
            </div>

            {/* More details collapse */}
            {showDetails && hasMoreDetails && (
                <div className="mt-4 space-y-3 border-t border-[#F1E9DC] pt-4">
                    {contract.comment && (
                        <div className="flex items-start gap-2 rounded-xl bg-cream p-3 text-sm text-[#6B675F]">
                            <FileText size={16} className="mt-0.5 shrink-0 text-brand" />
                            {contract.comment}
                        </div>
                    )}
                    {contract.reason && (
                        <div className="flex items-start gap-2 rounded-xl bg-cream p-3 text-sm text-[#6B675F]">
                            <Info size={16} className="mt-0.5 shrink-0 text-brand" />
                            Purpose: {contract.reason}
                        </div>
                    )}
                    <MeetingRoomQuota quota={contract.meetingRoomQuota} />
                    <DayPassConfig config={contract.dayPassConfig} />
                    {contract.void && contract.void.isVoid && (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                            <div className="mb-1 text-sm font-semibold text-red-800">Contract Voided</div>
                            <div className="text-sm text-red-700">Date: {formatDate(contract.void.voidDate)}</div>
                            {contract.void.voidReason && <div className="text-sm text-red-700">Reason: {contract.void.voidReason}</div>}
                        </div>
                    )}
                </div>
            )}

            {/* Booking details collapse */}
            {expanded && (
                <div className="mt-4 border-t border-[#F1E9DC] pt-4">
                    <div className="mb-3 font-display text-sm font-semibold text-ink">Booking Details</div>
                    {contract.bookings && contract.bookings.length > 0 ? (
                        <div className="space-y-3">
                            {contract.bookings.map(booking => <BookingDetails key={booking._id} booking={booking} />)}
                        </div>
                    ) : (
                        <div className="rounded-xl bg-cream p-4 text-center text-sm text-[#8C8175]">No booking details available</div>
                    )}
                </div>
            )}
        </div>
    );
};

const BookingDetails = ({ booking }) => {
    const location = booking.office ?
        `Office ${booking.office.number || booking.office._id.substring(0, 6)} (${booking.office?.code || 'NA'}), Floor ${booking.office.floor?.number || '-'} (${booking.office.floor?.floorCode || ''}), ${booking.office.floor?.building?.name || 'Building'}` :
        booking.seat ?
            `Seat ${booking.seat.seatCode || booking.seat._id.substring(0, 6)} - ${convertToPresentableText(booking.seat.seatType) || ''}, ${booking.seat.coworking?.floor?.building?.name || 'Building'}` :
            'Location not specified';

    const branch = booking.office ?
        booking.office.floor?.building?.branch?.name || 'Branch' :
        booking.seat ?
            booking.seat.coworking?.floor?.building?.branch?.name || 'Branch' : 'Branch not specified';

    const timings = booking.dailyStartTime && booking.dailyEndTime ?
        `${moment(booking.dailyStartTime).format('h:mm A')} - ${moment(booking.dailyEndTime).format('h:mm A')}` : 'Full Day';

    const cur = booking.rentCurrency || 'PKR';

    return (
        <div className="rounded-xl border border-[#ECE3D5] bg-cream p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-medium text-ink">{location}</div>
                    <div className="mt-0.5 flex items-center gap-1 text-sm text-[#8C8175]">
                        <MapPin size={13} /> {branch}
                    </div>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_BADGE[booking.status] || 'bg-cream-100 text-[#6B675F]'}`}>
                    {statusLabel(booking.status)}
                </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[#F1E9DC] pt-3 md:grid-cols-3">
                <Stat label="Period" value={`${formatDate(booking.startDate)} - ${formatDate(booking.endDate)}`} />
                <Stat label="Daily Hours" value={timings} />
                <Stat label="Space Type" value={booking.office ? 'Private Office' : 'Coworking Seat'} />
                <Stat label="Rent" value={`${booking.rentTotal?.toLocaleString() || '-'} ${cur}${booking.rentFrequency ? ` (${booking.rentFrequency})` : ''}`} />
                <Stat label="Advance" value={`${Number(booking.advanceAmount || 0).toLocaleString()} ${cur}`} />
                <Stat label="Security" value={`${Number(booking.securityAmount || 0).toLocaleString()} ${cur}`} />
            </div>
        </div>
    );
};

const MeetingRoomQuota = ({ quota }) => {
    if (!quota || !quota.monthlyHours) return null;
    const usedPercentage = (quota.usedHours / quota.monthlyHours) * 100;
    const lastReset = quota.lastResetDate ? moment(quota.lastResetDate).format('MMM DD, YYYY') : 'Not yet reset';

    return (
        <div className="rounded-xl bg-cream p-3">
            <div className="mb-1 text-sm font-semibold text-ink">Meeting Room Quota</div>
            <div className="mb-1 flex justify-between text-sm text-[#6B675F]">
                <span>{quota.usedHours} / {quota.monthlyHours} hours used</span>
                <span>{quota.meetingRoomHoursPerDay} hours/day</span>
            </div>
            <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-cream-100">
                <div
                    className={`h-2 rounded-full ${usedPercentage > 80 ? 'bg-red-500' : 'bg-brand'}`}
                    style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                />
            </div>
            <div className="flex justify-between text-xs text-[#8C8175]">
                <span>{quota.rolloverEnabled ? `Rollover enabled (max ${quota.rolloverLimit} hours)` : 'No rollover'}</span>
                <span>Last reset: {lastReset}</span>
            </div>
        </div>
    );
};

const DayPassConfig = ({ config }) => {
    if (!config || config.type === 'none') return null;
    const usedPercentage = config.maxDaysPerMonth ? (config.usedDays / config.maxDaysPerMonth) * 100 : 0;
    const lastReset = config.lastResetDate ? moment(config.lastResetDate).format('MMM DD, YYYY') : 'Not yet reset';

    return (
        <div className="rounded-xl bg-cream p-3">
            <div className="mb-1 text-sm font-semibold text-ink">Day Pass Configuration</div>
            <div className="mb-1 flex justify-between text-sm text-[#6B675F]">
                <span>Type: {convertToPresentableText(config.type)}</span>
                <span>{config.usedDays} / {config.maxDaysPerMonth} days used</span>
            </div>
            {config.maxDaysPerMonth > 0 && (
                <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-cream-100">
                    <div
                        className={`h-2 rounded-full ${usedPercentage > 80 ? 'bg-red-500' : 'bg-brand'}`}
                        style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                    />
                </div>
            )}
            {config.allowedDays && config.allowedDays.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1">
                    {config.allowedDays.map(day => (
                        <span key={day} className="rounded-full border border-[#E6DDCF] px-2 py-0.5 text-xs text-[#6B675F]">{day.substring(0, 3)}</span>
                    ))}
                </div>
            )}
            <div className="flex justify-between text-xs text-[#8C8175]">
                <span>{config.includesMeetingRoom ? 'Includes meeting room' : 'No meeting room included'}</span>
                <span>Last reset: {lastReset}</span>
            </div>
        </div>
    );
};

const EmptyState = ({ message, icon: Icon = FileText }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-[#ECE3D5] bg-white py-16 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cream-input text-brand">
            <Icon className="h-7 w-7" />
        </div>
        <h3 className="font-display text-lg font-bold text-ink">{message}</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-[#8C8175]">
            Your contracts will appear here once they are created. If you believe this is an error, please contact support.
        </p>
    </div>
);
